import { Component, EventListener, triggerPublicEvent, getComponentByDomNode } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { getCurrentViewport, VIEWPORT_ENTERED } from '@gondel/plugin-media-queries';
import { IGScrollHandlerOptions } from '@swisslife/shared-types/src/types/gondel-public-events';
import { Overlay } from '../../../organisms/overlay/js/overlay';
import { TextInput } from '../../../atoms/text-input/js/text-input';

import '../../../atoms/text-area/js/text-area';
import '../../../atoms/text-input/js/text-input';
import '../../../atoms/select/js/select';

import { IDatePickerApi } from './initFlatpickr';

enum States {
	DatePickerOpen = 'state-m-form-element--date-time-picker-open',
	DatePickerReady = 'state-m-form-element--date-time-picker-ready',
	Error = 'state-m-form-element--input-error',
}

enum Selectors {
	TextInput = '.js-a-text-input',
	TextInputInput = '.js-a-text-input__input',
	Backdrop = '.js-m-form-element__backdrop',
	DatePickerClearButton = '.js-m-form-element__date-picker-clear-button',
	Flatpickr = '.flatpickr-calendar',
	Overlay = '.js-o-page__overlay',
	Loader = '.js-o-form__loader',
}

type DatePickerEventCallbackFunction = (newDate: Date, newDateString: string) => any | never;

@Component('FormElement')
export class FormElement extends GondelJqueryComponent {
	private pluginInstance: IDatePickerApi;
	private isDatePicker: boolean;
	private isTimePicker: boolean;
	private isInOverlay: boolean = false;
	private overlay: Overlay;
	private fnCallbacksDatepickerChange: DatePickerEventCallbackFunction[] = [];
	private fnCallbacksDatepickerOpen: DatePickerEventCallbackFunction[] = [];

	public start() {
		const $overlay = this.$ctx.closest(Selectors.Overlay);
		if ($overlay.length > 0) {
			this.overlay = getComponentByDomNode<Overlay>($overlay.get(0));
			this.isInOverlay = true;
		}
	}

	public sync() {
		this.isDatePicker = this.$ctx.data('date-picker') || false;
		this.isTimePicker = this.$ctx.data('time-picker') || false;

		if (this.isDatePicker || this.isTimePicker) {
			(async () => {
				await this.initFlatpickr(this.$ctx);
			})();
		}
	}

	public get textInput(): TextInput | null {
		const $textInput = this.$ctx.find(Selectors.TextInput);
		if ($textInput.length === 0) {
			return null;
		}

		return getComponentByDomNode<TextInput>($textInput.get(0));
	}

	public setState(newState: string) {
		if (States[newState]) {
			this.$ctx.addClass(States[newState]);
		}
	}

	public removeState(stateToRemove: string) {
		if (this.$ctx.hasClass(stateToRemove)) {
			this.$ctx.removeClass(stateToRemove);
		}
	}

	public setOption(optionName: string, optionValue: string) {
		if (this.pluginInstance) {
			this.pluginInstance.setOption(optionName, optionValue);
		}
	}

	public getDate(): string {
		if (this.pluginInstance) {
			return this.pluginInstance.getDate();
		}

		return '';
	}

	public addDatePickerChangeCallback(fnCallback) {
		this.fnCallbacksDatepickerChange.push(fnCallback);
	}

	public removeDatePickerChangeCallback(fnCallback) {
		const index = this.fnCallbacksDatepickerChange.indexOf(fnCallback);

		if (index >= 0) {
			this.fnCallbacksDatepickerChange.splice(index, 1);
		}
	}

	public addDatePickerOpenCallback(fnCallback) {
		this.fnCallbacksDatepickerOpen.push(fnCallback);
	}

	public removeDatePickerOpenCallback(fnCallback) {
		const index = this.fnCallbacksDatepickerOpen.indexOf(fnCallback);

		if (index >= 0) {
			this.fnCallbacksDatepickerOpen.splice(index, 1);
		}
	}

	@EventListener('click', Selectors.Backdrop)
	public _handleDatePickerBackdropClick(ev: MouseEvent) {
		ev.preventDefault();
		// we need to manually close since it does not automatically close upon click on the backdrop
		if (this.pluginInstance) {
			this.pluginInstance.close();
		}
	}

	@EventListener('click', Selectors.DatePickerClearButton)
	public _handleDatePickerClearButtonClick(ev: MouseEvent) {
		ev.preventDefault();

		if (this.pluginInstance) {
			this.pluginInstance.clear();
			this.pluginInstance.close();
		}
	}

	@EventListener(VIEWPORT_ENTERED)
	public _handleViewportChangeEvent() {
		// close flatpickr to not need to maintain page / overlay scroll states
		if (this.pluginInstance) {
			this.pluginInstance.close();
		}
	}

	private initFlatpickr = async ($element: JQuery) => {
		// tslint:disable-next-line
		const asyncModule = await import(/* webpackChunkName: "dynamic-imports/flatpickr" */ './initFlatpickr');

		this.pluginInstance = asyncModule.initialize(
			$element,
			$element.find(Selectors.TextInputInput),
			this.isTimePicker ? 'time' : 'date',
			this.handleOpenCallback,
			this.handleCloseCallback,
			this.handleReadyCallback,
			this.handleChangeCallback
		);
	};

	private handleOpenCallback = (newDate: Date, newDateString: string) => {
		this.fnCallbacksDatepickerOpen.forEach((fnCallback) => fnCallback(newDate, newDateString));

		this.$ctx.addClass(States.DatePickerOpen);
		this.setScrollState(false);
	};

	private handleCloseCallback = () => {
		this.$ctx.removeClass(States.DatePickerOpen);
		this.setScrollState(true);
	};

	private handleReadyCallback = () => {
		this.$ctx.addClass(States.DatePickerReady);
		const $clearButton = this.$ctx.find(Selectors.DatePickerClearButton).get(0);
		this.$ctx.find(Selectors.Flatpickr).append($clearButton);
	};

	private handleChangeCallback = (newDate: Date, newDateString: string) => {
		this.fnCallbacksDatepickerChange.forEach((fnCallback) => fnCallback(newDate, newDateString));
	};

	private setScrollState(newState: boolean) {
		const currentVp = getCurrentViewport();

		if (currentVp === 'small' || currentVp === 'xsmall' || currentVp === 'xxsmall') {
			if (this.isInOverlay) {
				this.overlay.setScrolling(newState);
			} else {
				const eventOptions: IGScrollHandlerOptions = {
					scroll: newState,
				};

				triggerPublicEvent('gScrollHandler', this, undefined, eventOptions);
			}
		}
	}
}
